import React, { useEffect, useState } from 'react';
import { ArrowDown, Sparkles, Zap, Hexagon, Star, Shield } from 'lucide-react';

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };

    // Generate particles
    const particleArray = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 5,
    }));
    setParticles(particleArray);

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Gradient Mesh Background */}
      <div className="absolute inset-0 bg-gradient-mesh"></div>
      
      {/* Animated Grid Overlay */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-grid animate-grid-flow"></div>
      </div>

      {/* Floating Geometric Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-blue-400/30 to-purple-400/30 rounded-full blur-3xl animate-float-slow"
          style={{ transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)` }}
        ></div>
        <div 
          className="absolute top-40 right-20 w-80 h-80 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-full blur-3xl animate-float-slow"
          style={{ 
            transform: `translate(${-mousePosition.x}px, ${mousePosition.y}px)`,
            animationDelay: '2s'
          }}
        ></div>
        <div 
          className="absolute bottom-20 left-1/4 w-72 h-72 bg-gradient-to-br from-sky-400/30 to-indigo-400/30 rounded-full blur-3xl animate-float-slow"
          style={{ 
            transform: `translate(${mousePosition.x * 0.5}px, ${-mousePosition.y * 0.5}px)`,
            animationDelay: '4s'
          }}
        ></div>

        {/* Hexagon shapes */}
        <div className="absolute top-1/4 right-1/4 opacity-10 animate-spin-slow">
          <Hexagon className="w-32 h-32 text-blue-600" />
        </div>
        <div className="absolute bottom-1/3 left-1/3 opacity-10 animate-spin-slow" style={{ animationDelay: '3s' }}>
          <Hexagon className="w-24 h-24 text-cyan-600" />
        </div>
      </div>

      {/* Particle System */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-gradient-to-r from-blue-400 to-cyan-400 opacity-60 animate-particle-float"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`,
            }}
          ></div>
        ))}
      </div>

      {/* UNIQUE STRUCTURE - Asymmetric Layout */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          
          {/* LEFT SIDE - Main Content Cards */}
          <div className="space-y-6">
            {/* Top Icon Card - Floating */}
            <div className="flex justify-start mb-8 animate-bounce-in">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl blur-2xl opacity-50 animate-pulse-glow"></div>
                <div className="relative backdrop-blur-2xl bg-white/10 border border-white/20 rounded-3xl p-6 shadow-2xl">
                  <Sparkles className="w-12 h-12 text-white" />
                </div>
                {/* Orbiting particles */}
                <div className="absolute -top-2 -left-2 w-3 h-3 bg-cyan-400 rounded-full animate-orbit"></div>
                <div className="absolute -top-2 -right-2 w-2 h-2 bg-blue-400 rounded-full animate-orbit-reverse"></div>
              </div>
            </div>

            {/* Main Title Card - Large Glassmorphism */}
            <div className="relative animate-slide-up">
              <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-30 blur-2xl animate-border-flow"></div>
              <div className="relative backdrop-blur-2xl bg-white/10 border-2 border-white/20 rounded-[2.5rem] p-10 shadow-2xl">
                <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-white/10 to-transparent"></div>
                
                <div className="relative z-10">
                  <h1 className="text-6xl sm:text-7xl md:text-8xl font-bold tracking-tight mb-4">
                    <span className="relative inline-block">
                      <span className="iridescent-text-enhanced">
                        BIO-PEPTIDE
                      </span>
                      <span className="absolute inset-0 iridescent-text-enhanced blur-2xl opacity-50"></span>
                    </span>
                  </h1>
                  
                  <div className="text-3xl sm:text-4xl md:text-5xl font-light text-white/90 tracking-widest mb-6">
                    <span className="inline-block text-shadow-glow">PEN</span>
                  </div>

                  <p className="text-lg sm:text-xl text-white/70 leading-relaxed font-light max-w-xl">
                    Innovative Bio-Peptide Solutions for Enhanced Health and Performance
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Buttons Card */}
            <div className="flex flex-wrap gap-4 animate-slide-up stagger-2">
              <a
                href="#products"
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-2xl font-semibold text-lg shadow-2xl hover:shadow-cyan-500/50 transform hover:scale-110 transition-all duration-500 flex items-center gap-3 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <Zap className="w-6 h-6 relative z-10 group-hover:rotate-12 transition-transform duration-300" />
                <span className="relative z-10">Explore Products</span>
                <ArrowDown className="w-6 h-6 relative z-10 group-hover:translate-y-1 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              </a>
              
              <a
                href="#validation"
                className="group relative px-8 py-4 backdrop-blur-xl bg-white/10 border-2 border-white/30 text-white rounded-2xl font-semibold text-lg shadow-2xl hover:bg-white/20 hover:border-white/60 transform hover:scale-110 transition-all duration-500 flex items-center gap-3"
              >
                <Shield className="w-6 h-6 group-hover:rotate-180 transition-transform duration-500" />
                <span>Validate Product</span>
              </a>
            </div>
          </div>

          {/* RIGHT SIDE - Feature Cards in Unique Layout */}
          <div className="relative space-y-6">
            {/* Stats Cards - Stacked with offset */}
            <div className="grid grid-cols-2 gap-4">
              {/* Card 1 - Top Left */}
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6 shadow-xl transform hover:scale-105 transition-all duration-500 animate-slide-up stagger-3">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white mb-1">10K+</p>
                <p className="text-sm text-white/70">Happy Customers</p>
              </div>

              {/* Card 2 - Top Right (offset down) */}
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6 shadow-xl transform hover:scale-105 transition-all duration-500 mt-8 animate-slide-up stagger-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white mb-1">10+</p>
                <p className="text-sm text-white/70">Years Experience</p>
              </div>

              {/* Card 3 - Bottom Left (offset down) */}
              <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-6 shadow-xl transform hover:scale-105 transition-all duration-500 mt-4 animate-slide-up stagger-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white mb-1">5 Stars</p>
                <p className="text-sm text-white/70">Customer Reviews</p>
              </div>

              {/* Card 4 - Bottom Right */}
              <div className="backdrop-blur-xl bg-gradient-to-br from-blue-600/30 to-purple-600/30 border border-white/20 rounded-3xl p-6 shadow-xl transform hover:scale-105 transition-all duration-500 animate-slide-up stagger-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <p className="text-xs text-white/80 font-semibold">VERIFIED</p>
                </div>
                <p className="text-2xl font-bold text-white mb-1">100%</p>
                <p className="text-sm text-white/70">Authentic Products</p>
              </div>
            </div>

            {/* Large Feature Card - Bottom */}
            <div className="backdrop-blur-xl bg-gradient-to-br from-white/5 to-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl animate-slide-up stagger-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Premium Quality</h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Pharmaceutical-grade bio-peptides manufactured to the highest international standards.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced scroll indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex flex-col items-center">
          <div className="text-white/60 text-sm mb-2 animate-pulse">Scroll Down</div>
          <div className="flex flex-col items-center animate-bounce">
            <ArrowDown className="w-6 h-6 text-white/80 mb-2" />
            <div className="w-1 h-20 bg-gradient-to-b from-cyan-400/80 to-transparent rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Bottom gradient overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white to-transparent pointer-events-none z-10"></div>
    </section>
  );
};

export default Hero;
